import { Flex } from "@fluentui/react-northstar";
import React, { useEffect, useMemo, useState } from "react";
import i18n from "../../helper/i18n";
import { Dialog } from "@fluentui/react-northstar";
import { useTranslation } from "react-i18next";
import { useAiRoutes } from "../../App/useAiRoutes";

const SkillBridge = (props) => {
  const [showDialog, setShowDialog] = React.useState(false);
  const userRole = props?.user?.role || "";
  const userEmail = props?.user?.mail || props?.user?.email || "";
  const { skillBridgeData } = useAiRoutes(props.selectedExtension);

  const [iframeLoaded, setIframeLoaded] = useState(false);

  const { t } = useTranslation();

  const getCurrentLanguage = () => {
    const languageMappings = {
      en: "en",
      "en-GB": "en",
      "en-US": "en",
      es: "es",
      pt: "pt",
      de: "de",
      fr: "fr",
      hi: "hi",
      it: "it",
      ja: "ja",
    };
    return languageMappings[i18n.language];
  };

  const currentLanguage = getCurrentLanguage();

  const isDataLoading =
    !skillBridgeData || Object.keys(skillBridgeData).length === 0;

  const srcUrl = useMemo(() => {
    if (isDataLoading) return ""; // avoid loading iframe before data

    const base =
      "https://skillbridge-frontend-b5c9b4b5dghng7em.canadacentral-01.azurewebsites.net/";
    // const base = "https://localhost:3001";
    const mode =
      (Object.keys(skillBridgeData.additionalParams || {}).length &&
        (skillBridgeData.additionalParams?.Mode ||
          skillBridgeData.additionalParams?.mode)) ||
      "pre-prod";

    const normalizedSlug = props?.user?.slug?.replace(/^\?slug=/, "");
    const params = new URLSearchParams({
      slug: normalizedSlug || "",
      email: userEmail,
      language: currentLanguage,
      role: userRole,
      Mode: mode,
    });
    return `${base}?${params.toString()}`;
  }, [skillBridgeData, userRole, userEmail, currentLanguage]);

  const targetOrigin = useMemo(() => {
    if (!srcUrl) return "*";
    try {
      return new URL(srcUrl).origin;
    } catch (e) {
      return "*";
    }
  }, [srcUrl]);

  React.useEffect(() => {
    if (!props.selectedExtension?.skillBridge) {
      setShowDialog(true);
    }
  }, [props.selectedExtension]);

  if (!props.selectedExtension?.skillBridge) {
    return (
      <Dialog
        open={showDialog}
        onConfirm={() => setShowDialog(false)}
        confirmButton={t("startUp").controls.modal[0]}
        header="Skill Bridge"
        content={t("skillbridgeNotAvailable")}
        onCancel={() => setShowDialog(false)}
      />
    );
  }

  const postCrossDomainMessage = () => {
    const iframeEl = document.getElementsByClassName("skillbridge-app");

    // Force Microsoft Teams ONLY in Cypress
    const provider =
      typeof window !== "undefined" && window.Cypress
        ? "microsoft" // force Teams for tests
        : props?.user?.authType || ""; // normal users

    const payload = {
      source: "skillBridge",
      message: {
        isFromACV: true,
        auth: {
          type: provider,
          token: props?.user?.accessToken || "", // can be fake token in Cypress
        },
        user: {
          email: userEmail,
          role: userRole,
          slug: props?.user?.slug || "",
        },
      },
    };

    iframeEl[0]?.contentWindow?.postMessage(payload, targetOrigin);
  };

  useEffect(() => {
    if (iframeLoaded && !isDataLoading) {
      postCrossDomainMessage();
    }
  }, [
    iframeLoaded,
    isDataLoading,
    props?.user?.accessToken,
    props?.user?.authType,
    userEmail,
    userRole,
  ]);

  useEffect(() => {
    const handler = (e) => {
      if (
        e.data &&
        e.data.source === "skillBridge" &&
        e.data.message &&
        e.data.message.request === "auth"
      ) {
        const iframeEl = document.getElementsByClassName("skillbridge-app");

        // Force Microsoft Teams ONLY in Cypress
        const provider =
          typeof window !== "undefined" && window.Cypress
            ? "microsoft" // force Teams for tests
            : props?.user?.authType || ""; // normal users

        const payload = {
          source: "skillBridge",
          message: {
            isFromACV: true,
            auth: {
              type: provider,
              token: props?.user?.accessToken || "",
            },
            user: {
              email: userEmail,
              role: userRole,
              slug: props?.user?.slug || "",
            },
          },
        };
        iframeEl[0]?.contentWindow?.postMessage(payload, e.origin);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [props?.user, userEmail, userRole]);

  return (
    <Flex column style={{ height: "100vh" }}>
      <Flex style={{ flexGrow: 1 }}>
        <iframe
          src={srcUrl}
          className="skillbridge-app"
          style={{ width: "100%", borderTop: "none" }}
          allow="fullscreen; camera; microphone; clipboard-read; clipboard-write; display-capture"
          title="SkillBridge"
          onLoad={() => setIframeLoaded(true)}
          onError={(error) =>
            console.error("Error during authentication", error)
          }
        />
      </Flex>
    </Flex>
  );
};

export default SkillBridge;
